import { useEffect, useState } from 'react';

import { markerRegistry } from '@/lib/i18nOverrides/registry';

/*
 * Text edit mode on/off. A module-level store, not Redux: request interceptors (outside React) read it synchronously,
 * and it is one boolean of UI state. Not persisted — a reload leaves edit mode.
 */
type Listener = (enabled: boolean) => void;

const listeners = new Set<Listener>();
const state = { enabled: false };

export const getEditMode = (): boolean => state.enabled;

export const setEditMode = (enabled: boolean): void => {
  if (state.enabled === enabled) return;
  state.enabled = enabled;
  // Every toggle starts a fresh registry: indexes from the previous session would point at stale entries.
  // Cleared here, before any listener runs — not from a listener: in React 17 a listener's setState outside a React
  // event handler renders synchronously, so a later clearing listener would wipe the marks that render just registered.
  markerRegistry.clear();
  listeners.forEach((l) => l(enabled));
};

export const toggleEditMode = (): void => setEditMode(!state.enabled);

export const subscribeEditMode = (listener: Listener): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const useEditMode = (): boolean => {
  const [enabled, setEnabled] = useState(getEditMode);
  useEffect(() => {
    // The mode may have changed between the render and this effect.
    setEnabled(getEditMode());
    return subscribeEditMode(setEnabled);
  }, []);
  return enabled;
};
